import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { reportAppError } from '../../utils/errorReport';
import { accountsUsersUrl, shouldUseAccounts } from '../../accounts';
import styles from './Sidebar.module.css';

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, authConfig } = useAuth();
  const sso = shouldUseAccounts(authConfig);

  const handleLogout = async () => {
    try {
      await logout();
      if (!shouldUseAccounts(authConfig)) {
        navigate('/signin');
      }
    } catch (error) {
      reportAppError('שגיאה בהתנתקות', {
        action: 'Logout',
        error,
      });
      navigate('/signin');
    }
  };

  const menuItems = [
    {
      path: '/',
      label: 'הוצאות',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 3h18v18H3V3zm2 2v14h14V5H5zm2 2h10v2H7V7zm0 4h10v2H7v-2zm0 4h6v2H7v-2z" fill="currentColor" />
        </svg>
      ),
    },
    {
      path: '/tree',
      label: 'עץ קטגוריות',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.236L19 8v8L12 19.764 5 16V8l7-3.764zM12 6l-5 2.5v7L12 18l5-2.5v-7L12 6z"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      path: '/settings',
      label: 'הגדרות',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.49.49 0 00.12-.64l-1.92-3.32a.5.5 0 00-.61-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.5.5 0 00-.49-.42h-3.84a.5.5 0 00-.49.42l-.36 2.54c-.59.24-1.13.56-1.62.94l-2.39-.96a.5.5 0 00-.61.22L2.71 8.84a.49.49 0 00.12.64l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58a.49.49 0 00-.12.64l1.92 3.32c.12.22.37.29.61.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.49.41h3.84c.24 0 .44-.17.49-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.24.08.49 0 .61-.22l1.92-3.32a.49.49 0 00-.12-.64l-2.03-1.58zM12 15.6A3.6 3.6 0 1112 8.4a3.6 3.6 0 010 7.2z"
            fill="currentColor"
          />
        </svg>
      ),
    },
  ];

  const adminMenuItem = {
    path: '/admin',
    label: 'ניהול מערכת',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"
          fill="currentColor"
        />
      </svg>
    ),
  };

  const hasAdminAccess = sso || String(user?.tenant_id) === '1' || Boolean(user?.is_super_admin);
  const allMenuItems = hasAdminAccess ? [...menuItems, adminMenuItem] : menuItems;

  return (
    <aside className={styles.sidebar}>
      <div className={styles.logoSection}>
        <div className={styles.logo}>
          <span className={styles.logoText}>Metrica</span>
        </div>
        <div className={styles.logoSubtitle}>ניהול הוצאות</div>
      </div>

      <nav className={styles.navigation} aria-label="ניווט ראשי">
        {allMenuItems.map((item) => (
          sso && item.path === '/admin' ? (
            <a
              key={item.path}
              href={accountsUsersUrl(authConfig.identity_url)}
              className={styles.menuItem}
            >
              <span className={styles.menuIcon}>{item.icon}</span>
              <span className={styles.menuLabel}>{item.label}</span>
            </a>
          ) : (
          <Link
            key={item.path}
            to={item.path}
            className={`${styles.menuItem} ${
              location.pathname === item.path || (item.path === '/settings' && location.pathname === '/import')
                ? styles.active
                : ''
            }`}
          >
            <span className={styles.menuIcon}>{item.icon}</span>
            <span className={styles.menuLabel}>{item.label}</span>
          </Link>
          )
        ))}
      </nav>

      <div className={styles.userSection}>
        {user && (
          <div className={styles.userInfo}>
            <div className={styles.userName}>{user.name || user.email}</div>
            {user.email && user.name && <div className={styles.userEmail}>{user.email}</div>}
            {user.is_super_admin ? <div className={styles.userEmail}>Super admin</div> : null}
          </div>
        )}
        <button className={styles.logoutButton} onClick={handleLogout} type="button">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"
              fill="currentColor"
            />
          </svg>
          התנתק
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
